import React, { memo, useMemo } from 'react';
import {
    Button,
    IconButton,
    Menu,
    MenuButton,
    MenuList,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverTrigger,
    Tooltip
} from '@chakra-ui/react';
import { twMerge } from 'tailwind-merge';
import { BiChevronDown } from 'react-icons/bi';
import { FaEnvelope, FaFilter } from 'react-icons/fa';

import { Input } from 'common/materials';
import { TicketListItem } from 'models/link/Link';
import { parseDateString } from 'modules/common';

type Props = {
    tasks: TicketListItem[] | null;
    onClickTicketDetail: (task: TicketListItem) => void;
};

function HeaderLabel({ children, className = '' }: { children: string; className?: string }) {
    return (
        <Tooltip
            hasArrow
            placement="top"
            label={children}
        >
            <span className={twMerge('block truncate', className)}>{children}</span>
        </Tooltip>
    );
}

function TicketTable(props: Props) {
    const { tasks, onClickTicketDetail } = props;

    const taskGroups = useMemo(() => {
        if (!tasks) {
            return null;
        }

        const taskGroupMap = {};
        tasks.forEach(task => {
            const key = task.checkIn_Date;
            if (!taskGroupMap[key]) {
                taskGroupMap[key] = [task];
            } else {
                taskGroupMap[key].push(task);
            }
        });

        return Object.keys(taskGroupMap)
            .map(createdOn => ({
                createdOn,
                tasks: taskGroupMap[createdOn] as TicketListItem[]
            }))
            .reverse();
    }, [tasks]);

    if (!taskGroups) {
        return null;
    }

    return (
        <div className="w-full overflow-auto [&_*]:text-sm">
            <table className="w-full min-w-[1200px] table-fixed">
                <colgroup>
                    <col className="w-[25%]" />
                    <col className="w-[12%]" />
                    <col className="w-[13%]" />
                    <col className="w-[13%]" />
                    <col className="w-[10%]" />
                    <col className="w-[8%]" />
                    <col className="w-[7%]" />
                    <col className="w-[6%]" />
                    <col className="w-[6%]" />
                </colgroup>
                <thead>
                    <tr className="sticky top-0 whitespace-nowrap border border-zinc-300 bg-inherit bg-white [&_th:not(:first-child)]:px-4 [&_th]:overflow-hidden [&_th]:text-ellipsis [&_th]:border-l [&_th]:border-l-zinc-300 [&_th]:py-2">
                        <th>
                            <HeaderLabel>Title</HeaderLabel>
                        </th>
                        <th>
                            <HeaderLabel>Ticket No</HeaderLabel>
                        </th>
                        <th>
                            <HeaderLabel>Response Person</HeaderLabel>
                        </th>
                        <th>
                            <HeaderLabel>Activity Status</HeaderLabel>
                        </th>
                        <th>
                            <HeaderLabel>Work Group</HeaderLabel>
                        </th>
                        <th>
                            <div className="flex min-w-0 items-center gap-1 overflow-hidden">
                                <HeaderLabel className="min-w-0 flex-1">Progress</HeaderLabel>
                                <Popover placement="bottom-end">
                                    <PopoverTrigger>
                                        <IconButton
                                            size="xs"
                                            aria-label="filter-icon"
                                            variant="unstyled"
                                            className="group shrink-0 hover:bg-primary-900"
                                            icon={
                                                <FaFilter
                                                    size={16}
                                                    className="text-primary-900 transition-colors group-hover:text-white"
                                                />
                                            }
                                        />
                                    </PopoverTrigger>
                                    <PopoverContent className="w-fit">
                                        <PopoverArrow />
                                        <PopoverCloseButton />
                                        <PopoverBody className="flex gap-2">
                                            <Menu>
                                                <MenuButton
                                                    as={Button}
                                                    size="sm"
                                                    rightIcon={<BiChevronDown />}
                                                >
                                                    {/* {this.state.selectFilterProgress} */}
                                                </MenuButton>
                                                <MenuList>
                                                    {/* {this.filterOperators.map(operator => (
                                                        <MenuItem
                                                            key={operator}
                                                            onClick={() =>
                                                                this.changeState(
                                                                    'selectFilterProgress',
                                                                    operator
                                                                )
                                                            }
                                                        >
                                                            {operator}
                                                        </MenuItem>
                                                    ))} */}
                                                </MenuList>
                                            </Menu>
                                            <Input
                                                className="w-14"
                                                // onChange={event =>
                                                //     this.changeState(
                                                //         'inputFilterProgress',
                                                //         event.target.value
                                                //     )
                                                // }
                                            />
                                            <Button
                                                size="sm"
                                                // onClick={this.filterProgress}
                                            >
                                                Apply
                                            </Button>
                                        </PopoverBody>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </th>
                        <th>
                            <HeaderLabel>Issue Category</HeaderLabel>
                        </th>
                        <th>
                            <HeaderLabel>Issue Type</HeaderLabel>
                        </th>
                        <th>
                            <HeaderLabel>Issue Area</HeaderLabel>
                        </th>
                    </tr>
                </thead>
                {taskGroups.map(group => (
                    <tbody key={group.createdOn}>
                        <tr className="cursor-pointer border border-zinc-300 bg-[#FFF0D3] transition-colors duration-75 hover:bg-zinc-300 [&_*]:whitespace-nowrap [&_td]:px-4 [&_td]:py-1">
                            <td colSpan={99}>
                                <p className="flex-1 font-semibold text-neutral-900">
                                    {parseDateString(group.createdOn)}
                                </p>
                            </td>
                        </tr>
                        {group.tasks.map(task => (
                            <tr
                                key={task.aobjectlink}
                                className="cursor-pointer border border-zinc-300 transition-colors duration-75 hover:bg-zinc-300 [&_*]:whitespace-nowrap [&_td]:overflow-hidden [&_td]:text-ellipsis [&_td]:px-4 [&_td]:py-1"
                                onClick={() => onClickTicketDetail(task)}
                            >
                                <td>
                                    <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                                        <FaEnvelope
                                            size={14}
                                            className="shrink-0 text-primary-900"
                                        />
                                        <span
                                            title={task.show_radius}
                                            className="min-w-0 flex-1 truncate"
                                        >
                                            {task.show_radius}
                                        </span>
                                    </div>
                                </td>
                                <td title={task.ticketNO}>{task.ticketNO}</td>
                                <td title={task.leaderEN}>{task.leaderEN}</td>
                                <td className="text-center">
                                    <span
                                        className={twMerge(
                                            'rounded-full px-3 py-0.5',
                                            task.activityOverviewStatus === 'ถูกยกเลิก'
                                                ? 'border-red-600 bg-red-50 text-red-600'
                                                : task.activityOverviewStatus === 'ปิดงานแล้ว'
                                                ? 'border-green-600 bg-green-50 text-green-600'
                                                : task.activityOverviewStatus === 'กำลังดำเนินการ'
                                                ? 'border-blue-600 bg-blue-50 text-blue-600'
                                                : ''
                                        )}
                                    >
                                        {' '}
                                        {task.activityOverviewStatus}
                                    </span>
                                </td>
                                <td>
                                    <span
                                        title={task.projectcategory}
                                        className="block min-w-0 truncate"
                                    >
                                        {task.projectcategory}
                                    </span>
                                </td>
                                <td className="text-right">{parseInt(task.taskStatusPercent)} %</td>
                                {task.additionalField.length !== 0
                                    ? ['category', 'type', 'area'].map((type, index) => {
                                          const additionalField = task.additionalField.find(field =>
                                              field.fieldName.toLowerCase().includes(type)
                                          );

                                          return (
                                              <td
                                                  key={index}
                                                  title={additionalField?.optionsName}
                                              >
                                                  {additionalField?.optionsName}
                                              </td>
                                          );
                                      })
                                    : Array.from({ length: 3 }).map((_, index) => (
                                          <td key={index}></td>
                                      ))}
                            </tr>
                        ))}
                    </tbody>
                ))}
            </table>
        </div>
    );
}

export default memo(TicketTable);
